import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { formatDateTime } from "../lib/format";

const UNREAD_COUNT_POLL_MS = 20_000;

/** Bell icon + unread badge in the header, with a click-to-open dropdown of recent
 *  notifications. Only rendered for a signed-in user — see AppShell. */
export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: unread } = useQuery({
    queryKey: queryKeys.notificationsUnreadCount,
    queryFn: ({ signal }) => apiClient.getUnreadNotificationCount(signal),
    refetchInterval: UNREAD_COUNT_POLL_MS,
  });

  const { data: list } = useQuery({
    queryKey: queryKeys.notifications,
    queryFn: ({ signal }) => apiClient.getNotifications({ limit: 15 }, signal),
    enabled: isOpen,
    refetchInterval: isOpen ? 10_000 : false,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => apiClient.markNotificationRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notificationsUnreadCount });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => apiClient.markAllNotificationsRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notificationsUnreadCount });
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    function handleClickAway(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickAway);
    return () => document.removeEventListener("mousedown", handleClickAway);
  }, [isOpen]);

  function handleNotificationClick(notification: { id: string; readAt: string | null; linkPath: string | null }) {
    if (!notification.readAt) markRead.mutate(notification.id);
    setIsOpen(false);
    if (notification.linkPath) navigate(notification.linkPath);
  }

  const unreadCount = unread?.count ?? 0;

  return (
    <div className="notification-bell" ref={containerRef}>
      <button
        type="button"
        className="notification-bell__trigger"
        aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : "Notifications"}
        onClick={() => setIsOpen((v) => !v)}
      >
        🔔
        {unreadCount > 0 && (
          <span className="notification-bell__badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-bell__panel">
          <div className="row row--between notification-bell__header">
            <strong>Notifications</strong>
            {unreadCount > 0 && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={markAllRead.isPending}
                onClick={() => markAllRead.mutate()}
              >
                Mark all read
              </button>
            )}
          </div>

          {!list && <p className="text-sm muted notification-bell__empty">Loading…</p>}
          {list && list.items.length === 0 && (
            <p className="text-sm muted notification-bell__empty">Nothing yet.</p>
          )}
          {list && list.items.length > 0 && (
            <ul className="notification-bell__list">
              {list.items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    className={`notification-bell__item${n.readAt ? "" : " notification-bell__item--unread"}`}
                    onClick={() => handleNotificationClick(n)}
                  >
                    <span className="notification-bell__item-title">{n.title}</span>
                    <span className="text-sm muted">{n.body}</span>
                    <span className="text-sm muted">{formatDateTime(n.createdAt)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
